import {
  SimpleGrid,
  VStack,
  Spacer,
  Spinner,
  Center,
  Icon,
  Text,
  Flex,
  Box,
  Button,
} from '@chakra-ui/react';

import SectionContainer from '~/containers/SectionContainer';
import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';

import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import FooterLoginSection from '~/components/FooterLoginSection';
import PreOrderTimeBar from '~/components/PreOrderTimeBar';
import RestaurantCard from '~/components/RestaurantCard';
import LocationBar from '~/components/LocationBar';
import GoTopButton from '~/components/GoTopButton';
import BackwardBar from '~/components/BackwardBar';

import { AiOutlineShop } from 'react-icons/ai';
import { IoChevronForward } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import isHostSelector from '~/recoil/selectors/isHost';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import authAtom from '~/recoil/atom/auth';
import modalControlAtom from '~/recoil/atom/modalControl';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import useStoreList from '~/hooks/useStoreList';
import useCartId from '~/hooks/useCartId';

import * as fp from 'lodash/fp';
import parseISO from 'date-fns/parseISO';

import transformDateRangeToString from '~/utils/transformDateRangeToString';
import getScheduleTimeString from '~/utils/getScheduleTimeString';

import { FetchingDataStatus } from '~/types/utilities';
import { CartType } from '~/types/cart';
import EmptySection from '~/components/EmptySection';

export default function StoreList() {
  const auth = useRecoilValue(authAtom);
  const isHost = useRecoilValue(isHostSelector);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const pickMealEnd = useRecoilValue(pickMealEndAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const setModalControl = useSetRecoilState(modalControlAtom);

  const { cartType, cartId } = useCartId();

  const { groupId = '' } = useParams();
  const navigate = useNavigate();

  const { storeList, fetchingStatus } = useStoreList(currentPickMealPoint.stores);

  return (
    <PageContainer
      hideNavBar
      hideHeader
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => navigate(groupId ? `/group/${groupId}` : `/`)}
          />
          <LocationBar
            rightHandComponent={
              <Text fontSize="xs" color="white">
                你所選的取餐地點
              </Text>
            }
          />
          <PreOrderTimeBar
            pickMealTime={transformDateRangeToString(pickMealAt, pickMealEnd)}
            onClick={() =>
              !(fp.eq(cartType, CartType.CART_GROUP) && !isHost) &&
              setModalControl((prev) => ({ ...prev, isPickupSettingModalOPen: true }))
            }
            rightHandComponent={
              <Button
                size="sm"
                variant="unstyled"
                color="primary"
                fontSize="sm"
                rightIcon={<Icon as={IoChevronForward} />}
                letterSpacing="0.1em"
              >
                選擇取餐時間
              </Button>
            }
          />
        </>
      }
    >
      <Box minH="70vh">
        <Box bg="white">
          <Content minH="60vh">
            <VStack align="stretch" p={4} spacing={8}>
              <SectionContainer
                title="全部餐廳"
                icon={<Icon as={AiOutlineShop} color="secondary" boxSize="1.25rem" />}
              >
                {fp.eq(fetchingStatus, FetchingDataStatus.LOADING) && (
                  <Center py={4}>
                    <Spinner size="lg" />
                  </Center>
                )}

                {fp.eq(fetchingStatus, FetchingDataStatus.SUCCESS) &&
                  !fp.eq(storeList.length, 0) && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={3}>
                      {storeList.map((store) => {
                        return (
                          <RestaurantCard
                            key={store.objectID}
                            name={store.name}
                            image={store.image}
                            logo={store.logo}
                            time={getScheduleTimeString(
                              store.preOrderSetting.schedule,
                              store.officialHolidayISOString?.map((isoString) =>
                                parseISO(isoString),
                              ) || [],
                              pickMealAt,
                            )}
                            address={store.address}
                            onNavigate={() =>
                              navigate(
                                groupId
                                  ? `/group/${groupId}/store/${store.objectID}`
                                  : `/store/${store.objectID}`,
                                { state: { previous: window.location.pathname } },
                              )
                            }
                          />
                        );
                      })}
                    </SimpleGrid>
                  )}

                {fp.eq(fetchingStatus, FetchingDataStatus.SUCCESS) &&
                  fp.eq(storeList.length, 0) && <EmptySection content="目前無餐廳" />}

                {fp.eq(fetchingStatus, FetchingDataStatus.FAIL) && (
                  <EmptySection content="獲取餐廳發生錯誤，請重試" />
                )}
              </SectionContainer>
            </VStack>
          </Content>

          <Box position="sticky" bottom="0">
            <Box px={4}>
              <Content py={2}>
                <Flex>
                  <Spacer />
                  <GoTopButton />
                </Flex>
              </Content>
            </Box>

            <FooterReturnNotice />

            <>
              {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
                <FooterLoginSection />
              ) : (
                <FooterCartStatusBar
                  onNavigate={() =>
                    navigate(
                      fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/cart` : '/cart',
                    )
                  }
                />
              )}
            </>
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
}
